/**
 * opus-ia API Client
 *
 * Thin fetch wrapper with:
 * - x-request-id header on every request
 * - Authorization header injection from stored token
 * - JSON parsing with typed error handling
 * - Base URL from Vite env (falls back to /api)
 */

import {
  buildQuotaExceededFromDetails,
  buildQuotaExceededMessage,
  buildRateLimitMessage,
  parseRetryAfterSeconds,
} from './rateLimitMessage';

/** error_code of a 429 caused by an exhausted token quota (not by the rate limiter). */
const QUOTA_EXCEEDED_CODE = 'E_QUOTA_EXCEEDED';

export interface ApiError {
  status: number;
  message: string;
  code?: string;
  details?: unknown;
  /** Seconds to wait before retrying (429 only; from Retry-After, null when unknown). */
  retryAfterSeconds?: number | null;
}

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: unknown;
  public readonly retryAfterSeconds?: number | null;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = error.status;
    this.code = error.code;
    this.details = error.details;
    this.retryAfterSeconds = error.retryAfterSeconds;
  }
}

function generateRequestId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function getBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || '/api';
}

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('opus-ia-token');
  } catch {
    return null;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = await response.json();
    } catch {
      // response body is not JSON
    }
    const retryAfterSeconds =
      response.status === 429 ? parseRetryAfterSeconds(response.headers.get('Retry-After')) : undefined;
    const isQuotaExceeded = response.status === 429 && body['error_code'] === QUOTA_EXCEEDED_CODE;
    if (response.status === 429 && !isQuotaExceeded) {
      // Never surface the limiter's raw text: build the human message (FEAT-047 TASK-023).
      throw new ApiClientError({
        status: 429,
        message: buildRateLimitMessage(retryAfterSeconds ?? null),
        code: typeof body['error_code'] === 'string' ? body['error_code'] : 'E_RATE_LIMITED',
        details: body['details'],
        retryAfterSeconds,
      });
    }
    // A 429 E_QUOTA_EXCEEDED keeps the envelope message (pt-BR, with the renewal date in the org
    // time zone, FEAT-047 TASK-073): the "wait N hours" text of the limiter would hide it.
    const envelopeMessage = typeof body['message'] === 'string' ? body['message'] : null;
    // FEAT-054: with grace details the text comes from i18n (mode + percentage + renewal date);
    // without them (old server) the envelope message above is kept.
    const graceMessage = buildQuotaExceededFromDetails(response.status, body['error_code'], body['details']);
    throw new ApiClientError({
      status: response.status,
      message: isQuotaExceeded
        ? (graceMessage ?? envelopeMessage ?? buildQuotaExceededMessage())
        : (envelopeMessage ?? `Request failed with status ${response.status}`),
      retryAfterSeconds,
      // The API error envelope is {error_code, message, details, request_id}; `code` kept for older callers.
      code:
        typeof body['code'] === 'string'
          ? body['code']
          : typeof body['error_code'] === 'string'
            ? body['error_code']
            : undefined,
      details: body['details'],
    });
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  // Backend response shapes vary:
  //  (a) Single resource:    { data: { ...fields } }                  → unwrap to inner object
  //  (b) Paginated wrap:     { data: { items, total, page, ... } }    → unwrap + rename items→data
  //  (c) Cursor-paginated:   { data: [...], next_cursor, has_more }   → DO NOT unwrap (array + sibs)
  //  (d) List (envelope):    { data: [...] }                           → DO NOT unwrap (keep .data array)
  const json = (await response.json()) as unknown;
  if (
    typeof json === 'object' &&
    json !== null &&
    'data' in json
  ) {
    const obj = json as Record<string, unknown>;
    const inner = obj.data;
    const keys = Object.keys(obj);

    // Cases (c) / (d): data is already an array -- KEEP envelope so frontend reads .data
    if (Array.isArray(inner)) {
      return json as T;
    }

    // Case (a) / (b): inner is an object -- unwrap when envelope is small (<=3 keys)
    if (keys.length <= 3) {
      // Case (b): normalize { items, total, ... } → { data, total, ... }
      // EXCEPT for cursor-paginated responses ({ items, next_cursor, total_estimated })
      // which the frontend reads as .items directly (TanStack Query infinite query).
      if (
        inner &&
        typeof inner === 'object' &&
        'items' in inner &&
        Array.isArray((inner as { items: unknown[] }).items) &&
        !('next_cursor' in (inner as Record<string, unknown>))
      ) {
        const innerObj = inner as Record<string, unknown>;
        innerObj.data = innerObj.items;
        delete innerObj.items;
      }
      return inner as T;
    }
  }
  return json as T;
}

function buildHeaders(customHeaders?: HeadersInit): Headers {
  const headers = new Headers(customHeaders);

  headers.set('x-request-id', generateRequestId());
  headers.set('Content-Type', 'application/json');

  const token = getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

export const apiClient = {
  async get<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${getBaseUrl()}${path}`, {
      ...init,
      method: 'GET',
      headers: buildHeaders(init?.headers),
    });
    return handleResponse<T>(response);
  },

  async post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    const response = await fetch(`${getBaseUrl()}${path}`, {
      ...init,
      method: 'POST',
      headers: buildHeaders(init?.headers),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response);
  },

  async put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    const response = await fetch(`${getBaseUrl()}${path}`, {
      ...init,
      method: 'PUT',
      headers: buildHeaders(init?.headers),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response);
  },

  async patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    const response = await fetch(`${getBaseUrl()}${path}`, {
      ...init,
      method: 'PATCH',
      headers: buildHeaders(init?.headers),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response);
  },

  async delete<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${getBaseUrl()}${path}`, {
      ...init,
      method: 'DELETE',
      headers: buildHeaders(init?.headers),
    });
    return handleResponse<T>(response);
  },
};
